import { useMemo, useState, useEffect, useRef, useContext } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
    EAC_PHONE_TYPE, YES_NO_INVERSE, LANGUAGE_TYPE, VALIDATIONS, POSITIONS,
    MEMBER_OUTCOME_STATUS_VALUE, RECORD_STATUS_ABBR, ALERT_TYPE, US_STATE,
} from '../../../../constants';
import {
    optionValues, radioOptionValues, getMappedValuesFromPickList, todayEndofDay,
    dropdownValues, formatAddress,
} from '../../../../utils/utils';
import SharedForm from '../../../../components/ui/Form';
import Comment from '../../../../components/ui/Comment';
import Loader from '../../../../components/ui/Loader';
import { getPickListByCompanyId } from '../../../../ducks/company';
import {
    archiveMemberComment,
    getMemberCallDispositionData,
} from '../../../../ducks/advisoryGroup';
import messages from '../../../../utils/helper/messages';
import { AlertContext } from '../../../../context/AlertContext';

const OutreachMemberInfo = ({ data, onInputChange, onFormValidate, onDeleteComment, noPhoneChecked }) => {
    const dispatch = useDispatch();
    const hasFetchedPickList = useRef(false);
    const hasFetchedDisposition = useRef(false);
    const { showAlert } = useContext(AlertContext);

    const pickList = useSelector((state) => state.company?.pickList);
    const memberCallDispositionData = useSelector((state) => state.advisoryGroup?.callDispositionData);

    const comments = useSelector(state => state.advisoryGroup?.memberCommentList);

    const hideOtherLang = data?.applicationPrimaryLanguage !== LANGUAGE_TYPE.Other;
    const showInternetAccessComment = data?.deviceWithInternetAccess === YES_NO_INVERSE.No;
    const showNeedTranslator = data?.applicationPrimaryLanguage && data.applicationPrimaryLanguage !== LANGUAGE_TYPE.English;
    const currentUser = useSelector((state) => state.user.currentUser);
    const languageOptions = useMemo(() => {
        return pickList?.length > 0 ? getMappedValuesFromPickList(pickList, 'languageList') : LANGUAGE_TYPE;
    }, [pickList]);
    const stateOptions = useMemo(() => {
        const fromPickList = getMappedValuesFromPickList(pickList, 'stateList');
        return Object.keys(fromPickList).length > 0 ? fromPickList : US_STATE;
    }, [pickList]);
    const callDispositionData = useMemo(() => memberCallDispositionData?.length > 0 ? dropdownValues(memberCallDispositionData, 'callDisposition', undefined, true) : [], [memberCallDispositionData]);
    const initialCallStatus = optionValues(MEMBER_OUTCOME_STATUS_VALUE, true);
    const [showCommentBox, setShowCommentBox] = useState(false);

    useEffect(() => {
        if (!currentUser) return;

        if (!pickList || Object.keys(pickList)?.length === 0) {
            if (!hasFetchedPickList.current) {
                dispatch(getPickListByCompanyId(currentUser.companyId));
                hasFetchedPickList.current = true;
            }
        }
        if (!memberCallDispositionData || memberCallDispositionData?.length === 0) {
            if (!hasFetchedDisposition.current) {
                dispatch(getMemberCallDispositionData());
                hasFetchedDisposition.current = true;
            }
        }
    }, [currentUser, pickList, memberCallDispositionData, dispatch]);


    const memberInfoOutreachArr = [
        {
            key: 'outcome',
            labelName: 'Outcome',
            placeholderName: 'Outcome',
            type: 'options',
            optionValues: initialCallStatus,
            required: false,
        },
        {
            key: 'callDisposition',
            labelName: 'Call Disposition',
            type: 'options',
            optionValues: callDispositionData,
            required: false
        },
        {
            key: 'memberCommunicationLastDate',
            labelName: 'Last Outreach Date',
            type: 'date',
            required: false,
            maxValue: todayEndofDay()
        },
        {
            key: 'empaneledDate',
            labelName: 'Empaneled Date',
            type: 'date',
            required: false,
            maxValue: todayEndofDay()
        },
    ]


    const memberInfoArr = [
        {
            key: 'comment',
            labelName: showCommentBox ? 'Comments' : '',
            placeholderName: 'Comment',
            type: 'textarea',
            required: false,
            hidden: !showCommentBox,
            maxLength: 255,
            valid: VALIDATIONS.String.noSpecialChars,
            charCounter: true
        },
        {
            key: 'participateReason',
            labelName: 'Why are you interested in joining our Advisory Committee?',
            placeholderName: 'Please enter any comments you would like to share.',
            type: 'textarea',
            required: false,
            valid: VALIDATIONS.String.noSpecialChars,
            maxLength: 255,
            charCounter: true
        },
        {
            key: 'applicationHealthPlanId',
            labelName: 'Health Plan ID',
            placeholderName: 'Health Plan ID',
            type: 'text',
            required: false,
        },
        {
            key: 'applicationBirthday',
            labelName: 'Birthdate',
            placeholderName: '',
            type: 'date',
            maxValue: todayEndofDay(),
        },
        {
            key: 'applicationFirstName',
            labelName: 'First Name',
            placeholderName: 'First Name',
            type: 'text',
            maxLength: 35,
            valid: VALIDATIONS.String.isName,
        },
        {
            key: 'applicationLastName',
            labelName: 'Last Name',
            placeholderName: 'Last Name',
            type: 'text',
            maxLength: 35,
            valid: VALIDATIONS.String.isName,
        },
        {
            key: 'applicationAddress',
            labelName: 'Address Line 1',
            placeholderName: 'Address Line 1',
            type: 'text',
            maxLength: 35,
            copyGroup: ['applicationAddress', 'applicationAddress2', 'applicationCity', 'applicationState', 'applicationPostalCode'],
            copyAction: formatAddress,
        },
        {
            key: 'applicationAddress2',
            labelName: 'Address Line 2',
            placeholderName: 'Address Line 2',
            type: 'text',
            maxLength: 35,
            required: false
        },
        {
            key: 'applicationCity',
            labelName: 'City',
            placeholderName: 'City',
            type: 'text',
            maxLength: 50,
        },
        {
            key: 'applicationState',
            labelName: 'State',
            placeholderName: 'State',
            type: 'options',
            optionValues: optionValues(stateOptions, true),
            maxLength: 2
        },
        {
            key: 'applicationPostalCode',
            labelName: 'Zip',
            placeholderName: 'Zip',
            type: 'text',
            minLength: 5,
            maxLength: 9,
            valid: VALIDATIONS.Number.isZipCode
        },
        {
            key: 'applicationPhone',
            labelName: 'Phone',
            placeholderName: 'Phone',
            type: 'tel',
            maxLength: 12,
            valid: VALIDATIONS.Number.isPhone,
            cols: 6,
            disabled: noPhoneChecked,
            required: !noPhoneChecked,
            customError: 'Phone is required or else check the box below.',
            removeSpacing: true,
            dependsOn: "noPhone",
        },
        {
            key: 'applicationPhoneType',
            labelName: 'Phone Type',
            placeholderName: 'Phone Type',
            type: 'radio',
            optionValues: radioOptionValues(EAC_PHONE_TYPE, true),
            cols: 6,
            required: false,
            disabled: noPhoneChecked,
            removeSpacing: true
        },
        {
            key: 'noPhone',
            labelName: '',
            type: 'checkbox',
            optionValues: [
                { id: 'noPhoneCheck', label: 'I do not have a phone number', value: "noPhone" },
            ],
            required: false,
            disabled: !!data?.applicationPhone,
        },
        {
            key: 'applicationEmail',
            labelName: 'Email',
            placeholderName: 'Email',
            type: 'email',
            required: false,
            maxLength: 255
        },
        {
            key: 'applicationPrimaryLanguage',
            labelName: 'Primary Language',
            placeholderName: 'Primary Language',
            type: 'options',
            optionValues: optionValues(languageOptions, true),
            maxLength: 50,
        },
        {
            key: "primaryLanguageOther",
            labelName: "Specify Language",
            placeholderName: "Specify Language",
            type: "text",
            maxLength: 50,
            hidden: hideOtherLang,
            required: !hideOtherLang,
        },
        {
            key: 'needTranslator',
            labelName: 'Need Translator?',
            placeholderName: 'Need Translator?',
            type: 'radio',
            hidden: !showNeedTranslator,
            required: showNeedTranslator,
            optionValues: radioOptionValues(YES_NO_INVERSE, true)
        },
        {
            key: 'deviceWithInternetAccess',
            labelName: 'Device with Internet Access',
            type: 'radio',
            required: false,
            optionValues: radioOptionValues(YES_NO_INVERSE, true)
        },
        {
            key: "deviceWithInternetAccessComment",
            labelName: "",
            placeholderName: "Provide any additional information.",
            type: "text",
            required: false,
            maxLength: 50,
            hidden: !showInternetAccessComment,
            valid: VALIDATIONS.String.noSpecialChars,
            charCounter: true
        },
    ];

    const handleAddComment = () => setShowCommentBox(true);

    const handleDeleteSuccess = () => {
        showAlert(messages.success.deleteOutreachComment, ALERT_TYPE.SUCCESS);
        onDeleteComment();
    }

    const handleError = (error) => {
        showAlert(`${messages.error.prefix} ${error}.`, ALERT_TYPE.ERROR);
    }

    const handleDelete = (id) => {
        const deleteCommentData = {
            id: id,
            status: RECORD_STATUS_ABBR.Archived,
        };
        dispatch(archiveMemberComment(deleteCommentData, handleDeleteSuccess, handleError));
    }

    return (
        <div className="px-xl-2 py-xl-2">
            <SharedForm
                cols={12}
                array={memberInfoOutreachArr}
                modelObj={data}
                actionBarPosition={POSITIONS.NONE}
                onInputChanged={onInputChange}
                onFormValidate={onFormValidate}
            />
            <div className='mb-3'>
                {comments === null ? (
                    <Loader size={2} />
                ) : (
                    <Comment
                        comments={comments}
                        onAddComment={handleAddComment}
                        commentBox={showCommentBox}
                        onDelete={handleDelete}
                    />
                )}
            </div>
            <SharedForm
                cols={12}
                array={memberInfoArr}
                modelObj={data}
                onInputChanged={onInputChange}
                actionBarPosition={POSITIONS.NONE}
                onFormValidate={onFormValidate}
            />
        </div>
    )
}

export default OutreachMemberInfo;
